/**
 * The person's own model key for Flow's assistant (sandbox-first, docs/SANDBOX_FIRST.md), kept by mia-flow-bff
 * (`GET/PUT/DELETE {bffUrl}/api/me/model-key`). The browser only sends the key once, to save it: the BFF checks it
 * with one small request and answers with what can be shown (`{ configured, provider, last4, baseUrl, project, region,
 * clientEmail, checkedAt }`). Nothing here logs or keeps the key.
 *
 * Google Vertex AI takes a service-account JSON key file (sent as `apiKey`), a project ID and a region instead.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

export const MODEL_PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'vertex', label: 'Google Vertex AI' },
  { value: 'openai-compatible', label: 'OpenAI-compatible URL' },
];

/** Common Vertex AI locations; any other location name can be typed. */
export const VERTEX_REGIONS = [
  'us-central1', 'us-east5', 'us-east4', 'us-west1', 'europe-west1', 'europe-west4', 'europe-west8', 'europe-west9',
  'asia-northeast1', 'asia-southeast1', 'global',
];
export const DEFAULT_VERTEX_REGION = 'us-central1';
/** The BFF refuses larger files; a service-account key file is about 2.4 KB. */
export const MAX_SERVICE_ACCOUNT_BYTES = 16 * 1024;

/**
 * What the browser can tell about a pasted or chosen service-account key file before sending it:
 * `{ projectId, clientEmail }`, or `{ error }`. Never echoes the file.
 */
export function readServiceAccount(text) {
  const raw = String(text || '').trim();
  if (!raw) { return { error: null }; }
  if (new Blob([raw]).size > MAX_SERVICE_ACCOUNT_BYTES) { return { error: 'This file is too large to be a service account key.' }; }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { error: 'This is not a JSON key file.' };
  }
  if (!parsed || parsed.type !== 'service_account') {
    return { error: 'This is not a service account key file.' };
  }
  if (!parsed.client_email || !parsed.private_key) {
    return { error: 'This key file has no client_email or private_key.' };
  }
  return { projectId: typeof parsed.project_id === 'string' ? parsed.project_id : '', clientEmail: parsed.client_email };
}

export class ModelKeyError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ModelKeyError';
    this.status = status;
  }
}

const url = () => `${String(config.bffUrl).replace(/\/$/, '')}/api/me/model-key`;

async function request(method, body) {
  const response = await getAuthorizedFetch()(url(), {
    method,
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const answer = await response.json().catch(() => null);
    throw new ModelKeyError(answer?.error || answer?.message || `The key could not be saved (${response.status})`, response.status);
  }
  return response.json();
}

/** What is saved, or `null` when this Flow cannot keep a key (no BFF, or one too old to know the route). */
export async function getModelKey() {
  if (!config.bffUrl) { return null; }
  try {
    return await request('GET');
  } catch (error) {
    if (error.status === 404) { return null; }
    throw error;
  }
}

/** Checks and saves a key. Rejects with the BFF's reason (400) when the provider refused it. */
export function saveModelKey({ provider, apiKey, baseUrl, project, region }) {
  if (provider === 'vertex') {
    return request('PUT', { provider, apiKey, project, region });
  }
  return request('PUT', { provider, apiKey, ...(provider === 'openai-compatible' && baseUrl ? { baseUrl } : {}) });
}

/** Forgets the saved key. */
export function removeModelKey() {
  return request('DELETE');
}
